// ═══════════════════════════════════════════════════════════════════
// Horizon Protocol - v2 theme
//
// Deep cosmic navy surfaces, moon-silver text, gold accent for HORIZON
// identity. Semantic colors (positive/negative) kept sparse - colour
// signals identity, not decoration.
// ═══════════════════════════════════════════════════════════════════

// Fine monochrome grain for box surfaces - an inline SVG feTurbulence (self-contained, no asset,
// CSP-safe). Layered over a panel's solid colour it adds a subtle "material" texture and breaks up
// flat-dark banding. baseFrequency = grain size; the rect's opacity = intensity.
// Panel surface texture. Was an feTurbulence film-grain at 0.06 opacity with numOctaves=2 - the coarse
// second octave plus the 160px tile repeating read as soft cloudy blotches on large panels rather than
// an even grain, so it is off (flat panels). To bring back a CLEAN fine grain instead of removing it,
// restore a single-octave, higher-frequency, lower-opacity turbulence here.
const NOISE = 'none'

export const T = {
  // ─── Base surfaces ─────────────────────────────────────────
  bg:           '#060A14',
  bg2:          'rgba(11, 16, 32, 0.55)',
  surface:      'rgba(16, 22, 40, 0.55)',
  surfaceHover: 'rgba(22, 30, 52, 0.70)',
  border:       'rgba(255, 255, 255, 0.09)',
  borderHover:  'rgba(91, 158, 255, 0.45)',  // blue focus/hover (was silver)

  // ─── Glass - backdrop-filter driven translucency ───────────
  // Cards, drawers, topbar all share the same blur/saturate stack
  // so depth reads consistently as you scroll past the moon + stars.
  glassBlur:    'none',
  glassInset:   'inset 0 1px 0 rgba(255, 255, 255, 0.06)',
  glassHairline:'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 60%)',

  // ─── Text ──────────────────────────────────────────────────
  text:      '#F0F4FF',
  textMuted: '#C0C8DC',
  textDim:   '#7C86A0',

  // ─── Semantic palette ─────────────────────────────────────
  // accent    moonlight silver, primary brand (cold luminous
  //           silver-blue that fits the cosmic navy backdrop -
  //           swapped from pitch-deck gold #FFD166 in v2.3)
  // positive  emerald, yields
  // warning   amber, caution zone (LTV health, etc.)
  // negative  coral, burns / losses / danger zone
  // cyan      treasury / neutral info
  // purple    secondary accent (farm rewards, share-card highlights)
  // Blue palette (Horizon is blue): the accent is now HORIZON blue rather than moonlight silver, so
  // every accent surface — primary CTA gradient, links, active pills, hero numbers, focus rings —
  // resolves blue through this one token. (Was silver #B8C6E0 / #8CA2C7 / #D4DEEE.)
  accent:      '#5B9EFF',
  accentDeep:  '#3D8BFF',
  accentLight: '#9CC6FF',
  accentDim:   'rgba(91, 158, 255, 0.14)',

  // TitanX-style vivid green (was #10B981 emerald, then #1FD97B mint - both leaned teal from too
  // much blue). This drops the blue channel right down for a strong, near-lime green. It's the one
  // token every green "font" across the app resolves through - APYs, positive deltas, up-values,
  // the "New" badges - so setting it here greens the whole frontend at once.
  positive:    '#2DE64B',
  positiveDim: 'rgba(45, 230, 75, 0.12)',

  warning:     '#F59E0B',
  warningDim:  'rgba(245, 158, 11, 0.12)',

  negative:    '#F87171',
  burn:        '#F87171',
  burnDim:     'rgba(248, 113, 113, 0.12)',

  cyan:        '#38BDF8',
  cyanDim:     'rgba(56, 189, 248, 0.12)',

  purple:      '#A78BFA',
  purpleDim:   'rgba(167, 139, 250, 0.12)',

  // ─── Fonts ─────────────────────────────────────────────────
  // Single typeface (Chakra Petch — the geometric/techno face from the horizon.win
  // marketing site) across every role; per-element `fontWeight` picks 300–700.
  // Numeric columns rely on the `.mono` class setting `font-variant-numeric:
  // tabular-nums` rather than a separate monospace family - keeps the voice uniform.
  fontD: "'Chakra Petch', system-ui, -apple-system, sans-serif",
  fontM: "'Chakra Petch', system-ui, -apple-system, sans-serif",
  fontN: "'Chakra Petch', system-ui, -apple-system, sans-serif",

  // ─── Spacing ───────────────────────────────────────────────
  sp: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 },

  // ─── Radius ────────────────────────────────────────────────
  r: { sm: 0, md: 0, lg: 0, pill: 999 },

  // ─── Type scale ────────────────────────────────────────────
  fs: { caption: 12, body: 13, ui: 15, heading: 22, display: 40 },  // caption 11->12 for legibility (a11y review)

  // ─── Depth ─────────────────────────────────────────────────
  // Softer + more diffuse than opaque-metal shadows so frosted cards
  // read as floating above the moon/stars backdrop.
  shadowLow:  '0 2px 10px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  shadowMed:  '0 10px 32px rgba(0, 0, 0, 0.40), inset 0 1px 0 rgba(255, 255, 255, 0.07)',
  shadowHigh: '0 20px 60px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
  shadowGold: '0 0 32px rgba(91, 158, 255, 0.24), inset 0 1px 0 rgba(156, 198, 255, 0.12)',  // blue accent glow

  // ─── Translucent surfaces (used by Card/TopBar/drawer) ────
  glassSurface:      '#111E33', // elevated navy-blue card (was #17263F, too light)
  glassSurfaceHover: '#182842',
  glassButton:       '#182034',
  glassButtonHover:  '#212B45',
  glassButtonActive: '#0E1220',
  noise:             NOISE,

  // ─── Local panel glass. Single source for the dark-glass tone
  //     that every page uses on local panels (Stake / Auction /
  //     Payouts / Stats secondary sections). Previously duplicated
  //     as a local GLASS const in each page.
  panelGlass: {
    background: '#0B1628', // darker navy-blue panel (was #0F1D38, too light); no texture
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  },
}
